import { Combobox as BaseCombobox } from "@base-ui/react/combobox";
import { Field as BaseField } from "@base-ui/react/field";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useMemo, useState, type ReactNode } from "react";
import { useDebouncedValue } from "@/lib/hooks";
import { FieldFooter, FieldLabel, type FieldLabelProps } from "../Field/FieldParts";
import { Icon } from "../Icon/Icon";
import { Spinner } from "../Spinner/Spinner";
import fieldStyles from "../Field/Field.module.css";
import styles from "../Popup/Popup.module.css";

export interface ComboboxProps<T> extends FieldLabelProps {
  value: T | null;
  onValueChange: (value: T | null) => void;
  /** Text for an option: shown in the input and used to filter static options. */
  getOptionLabel: (option: T) => string;
  /** Stable identity of an option (usually its id). */
  getOptionKey: (option: T) => string;
  /** Custom row content, e.g. the material name with its CAS number underneath. */
  renderOption?: (option: T) => ReactNode;
  /** A fixed list, filtered as the user types. */
  options?: readonly T[];
  /** Server search, called with the trimmed query after a 200 ms pause; results are cached. */
  loadOptions?: (query: string, signal: AbortSignal) => Promise<T[]>;
  /** Cache key prefix for loadOptions results, e.g. ["materials", "search"]. */
  queryKey?: readonly unknown[];
  /** Characters needed before loadOptions runs (default 1). */
  minQueryLength?: number;
  placeholder?: string;
  /** Shown when nothing matches; defaults to “No matches for …”. */
  emptyMessage?: string;
  size?: "sm" | "md" | "lg";
  name?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

/**
 * A searchable single-choice input (Base UI Combobox). Pass `options` for a local list or
 * `loadOptions` for server search ("Recognition rather than recall": never type an id).
 */
export function Combobox<T>({
  label,
  hideLabel,
  description,
  error,
  optional,
  value,
  onValueChange,
  getOptionLabel,
  getOptionKey,
  renderOption,
  options,
  loadOptions,
  queryKey,
  minQueryLength = 1,
  placeholder,
  emptyMessage,
  size = "md",
  name,
  disabled,
  required,
  className,
}: ComboboxProps<T>) {
  const [searchText, setSearchText] = useState("");
  const query = useDebouncedValue(searchText.trim(), 200);
  const searching = loadOptions !== undefined;
  const searchEnabled = searching && query.length >= minQueryLength;

  const search = useQuery({
    queryKey: [...(queryKey ?? ["combobox"]), "options", query],
    queryFn: ({ signal }) => (loadOptions ? loadOptions(query, signal) : Promise.resolve([])),
    enabled: searchEnabled,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const items = useMemo<readonly T[]>(() => {
    if (!searching) return options ?? [];
    const results = searchEnabled ? (search.data ?? []) : [];
    if (value !== null && !results.some((option) => getOptionKey(option) === getOptionKey(value))) {
      return [...results, value];
    }
    return results;
  }, [searching, options, searchEnabled, search.data, value, getOptionKey]);

  let status: ReactNode = null;
  if (searching) {
    if (searchText.trim().length < minQueryLength) {
      status = value === null ? "Type to search…" : null;
    } else if (search.isError) {
      status = "Search failed. Keep typing to try again.";
    } else if (search.isFetching && (search.data === undefined || search.isPlaceholderData)) {
      status = (
        <>
          <Spinner size="sm" label={null} />
          Searching…
        </>
      );
    } else if (search.data?.length === 0) {
      status = emptyMessage ?? `No matches for “${query}”.`;
    }
  }

  return (
    <BaseField.Root
      name={name}
      disabled={disabled}
      invalid={error ? true : undefined}
      className={clsx(fieldStyles.field, className)}
    >
      <BaseCombobox.Root<T>
        items={items}
        value={value}
        onValueChange={(next) => onValueChange(next)}
        onInputValueChange={(next, details) => {
          if (details.reason !== "item-press") setSearchText(next);
        }}
        itemToStringLabel={getOptionLabel}
        itemToStringValue={getOptionKey}
        isItemEqualToValue={(item, selected) => getOptionKey(item) === getOptionKey(selected)}
        filter={searching ? null : undefined}
        name={name}
        disabled={disabled}
        required={required}
      >
        <FieldLabel label={label} hideLabel={hideLabel} optional={optional} />
        <BaseCombobox.InputGroup className={fieldStyles.control} data-size={size}>
          <BaseCombobox.Input className={fieldStyles.input} placeholder={placeholder} />
          {value !== null ? (
            <BaseCombobox.Clear className={fieldStyles.stepper} aria-label="Clear">
              <Icon name="x" />
            </BaseCombobox.Clear>
          ) : null}
          <BaseCombobox.Trigger className={fieldStyles.stepper} aria-label="Show options">
            <Icon name="chevronDown" />
          </BaseCombobox.Trigger>
        </BaseCombobox.InputGroup>
        <BaseCombobox.Portal>
          <BaseCombobox.Positioner className={styles.positioner} sideOffset={4} collisionPadding={8}>
            <BaseCombobox.Popup className={styles.popup} aria-busy={search.isFetching || undefined}>
              <BaseCombobox.Status className={styles.status}>{status}</BaseCombobox.Status>
              {!searching ? (
                <BaseCombobox.Empty className={styles.status}>{emptyMessage ?? "No matches."}</BaseCombobox.Empty>
              ) : null}
              <BaseCombobox.List className={styles.list}>
                {(option: T) => (
                  <BaseCombobox.Item key={getOptionKey(option)} value={option} className={styles.item}>
                    <BaseCombobox.ItemIndicator className={styles.itemIndicator}>
                      <Icon name="check" />
                    </BaseCombobox.ItemIndicator>
                    <span className={styles.itemText}>
                      {renderOption ? renderOption(option) : getOptionLabel(option)}
                    </span>
                  </BaseCombobox.Item>
                )}
              </BaseCombobox.List>
            </BaseCombobox.Popup>
          </BaseCombobox.Positioner>
        </BaseCombobox.Portal>
      </BaseCombobox.Root>
      <FieldFooter description={description} error={error} />
    </BaseField.Root>
  );
}
